/**
 * Represents the saved state of the catalog page for "back to catalog" navigation.
 * Stored in sessionStorage under the key 'catalogState'.
 */
export interface CatalogState {
    /** The full URL of the catalog page (including query params) */
    url: string;
    /** The scroll position (window.scrollY) when the user left the catalog */
    scrollPosition: number;
    /** The number of items that were loaded in the catalog list */
    itemsLoaded: number;
}

export const CATALOG_STATE_KEY = 'catalogState';

/**
 * Retrieves the saved catalog state from session storage.
 * Returns null if no state is saved or if parsing fails.
 */
export function getCatalogState(): CatalogState | null {
    if (typeof window === 'undefined') return null;

    try {
        const stored = sessionStorage.getItem(CATALOG_STATE_KEY);
        if (!stored) return null;
        return JSON.parse(stored) as CatalogState;
    } catch {
        return null;
    }
}

/**
 * Clears the saved catalog state from session storage.
 */
export function clearCatalogState(): void {
    if (typeof window === 'undefined') return;
    try {
        if (sessionStorage.getItem(CATALOG_STATE_KEY) == null) return;
        sessionStorage.removeItem(CATALOG_STATE_KEY);
    } catch (error) { }
}

/**
 * Compares two URLs, ignoring the 'loaded' query parameter.
 */
export function urlsMatchIgnoringParams(url1: string, url2: string): boolean {
    try {
        const u1 = new URL(url1);
        const u2 = new URL(url2);

        u1.searchParams.delete('loaded');
        u1.searchParams.delete('restore');
        u2.searchParams.delete('loaded');
        u2.searchParams.delete('restore');

        return u1.toString() === u2.toString();
    } catch {
        return false;
    }
}
